import { Box, FormControl, MenuItem, Select } from '@mui/material';
import PropTypes from 'prop-types';

import Text from '../../elements/text/Text';

/**
 * SelectDropdown component
 * @param title
 * @param selectList - list of items to select from
 * @param value - current selected value. If not provided, the first item in the list will be selected
 * @param valueKey - key to use as value in the select dropdown
 * @param nameKey - key to use as name in the select dropdown
 * @param prefix - prefix to add to the name of the items in the select dropdown
 * @param onChange - function to call when the selected value changes
 * @param topMargin - add top margin to the component
 * @param noPrefix - if true, the prefix will not be added to the name of the items in the select dropdown
 * @param ariaLabel - aria label for the select dropdown
 * @param isControlled - There are two types of select dropdowns: controlled and uncontrolled.
 * controlled: the value is passed as a prop and the parent component is responsible for updating the value in a react state
 * uncontrolled: the value is managed by the select dropdown itself
 * @returns {JSX.Element}
 */
const SelectDropdown = ({
  title,
  selectList,
  value,
  valueKey,
  nameKey,
  prefix = '',
  onChange,
  topMargin,
  noPrefix,
  ariaLabel = 'selection switch',
  isControlled = false,
}) => {
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const getDefaultValue = () => {
    if (value) return value;
    return valueKey ? selectList[0][valueKey] : selectList[0];
  };

  const items = selectList.map((item) => (
    <MenuItem
      key={valueKey ? item[valueKey] : item}
      value={valueKey ? item[valueKey] : item}
    >
      {noPrefix
        ? nameKey
          ? `${item[nameKey]}`
          : `${item}`
        : nameKey
          ? `${prefix}-${item[nameKey]}`
          : `${prefix}${item}`}
    </MenuItem>
  ));

  return (
    <Box sx={{ display: 'flex', mt: topMargin ? 2 : 0 }}>
      {title && <Text text={title} sx={{ alignSelf: 'center', mr: 1 }} />}
      <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
        {isControlled ? (
          <Select
            onChange={handleChange}
            value={getDefaultValue()}
            inputProps={{ 'aria-label': ariaLabel }}
            variant={'outlined'}
          >
            {items}
          </Select>
        ) : (
          <Select
            onChange={handleChange}
            defaultValue={getDefaultValue()}
            inputProps={{ 'aria-label': ariaLabel }}
            variant={'outlined'}
          >
            {items}
          </Select>
        )}
      </FormControl>
    </Box>
  );
};

SelectDropdown.propTypes = {
  title: PropTypes.string,
  selectList: PropTypes.array.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  valueKey: PropTypes.string,
  nameKey: PropTypes.string,
  prefix: PropTypes.string,
  topMargin: PropTypes.bool,
  noPrefix: PropTypes.bool,
  ariaLabel: PropTypes.string,
  isControlled: PropTypes.bool,
};

export default SelectDropdown;
